/**
 * The phone's live view of its processing site and packing hall: the downloaded site pack with
 * this phone's own F6/F7 commands laid on top (those the server had not yet received when the
 * pack was downloaded), plus what sync said about them.
 *
 * PURE: `projectProcess(stored, outbox)` → ProcessState. The server stays the authority — it
 * re-checks every rule when a command is applied and the next download replaces this view.
 */
import type { OutboxRecord } from '@/offline/db'
import type {
  LastRun,
  Machine,
  PackLot,
  PackRun,
  RunBalance,
  RunStatus,
  SiteBag,
  SitePack,
  SiteRun,
  SiteUnit,
  Step,
  StoredSitePack,
} from '@/offline/sitePack'
import type {
  BindMachineLabelPayload,
  CancelRunPayload,
  CloseIssuePayload,
  CloseRunPayload,
  ConfirmCleanoutPayload,
  CorrectRunCommandPayload,
  CorrectRunPayload,
  DecideRunPayload,
  IssueToRunPayload,
  OpenRunPayload,
  RecordOutputPayload,
  ReportMixingPayload,
  SealSamplePayload,
  VoidOutputPayload,
  WeighInPayload,
} from './commands'
import type {
  PackClosePayload,
  VerifyPouchPayload,
  WeighPouchesPayload,
} from '@/features/pack/commands'

export const PROCESS_TYPES: ReadonlySet<string> = new Set([
  'bind_machine_label',
  'open_run',
  'cancel_run',
  'issue_to_run',
  'close_issue',
  'confirm_cleanout',
  'weigh_in',
  'report_mixing',
  'record_output',
  'void_output',
  'close_run',
  'seal_sample',
  'correct_run',
  'decide_run',
  'verify_pouch',
  'weigh_pouches',
  'pack_close',
])

export interface SyncMark {
  /** The newest not-yet-delivered command for this thing (later commands depend on it). */
  pending_command_id: string | null
  held: boolean
  error: boolean
  exception_ids: string[]
}

const emptyMark = (): SyncMark => ({
  pending_command_id: null,
  held: false,
  error: false,
  exception_ids: [],
})

export interface BagView extends SiteBag {
  /** The run this bag was issued to (server or this phone). */
  issued_run_id: string | null
  /** Net weight at the issue (the run's input weight). */
  issue_net_g: number | null
}

export interface RunInputView {
  id: string
  container_id: string | null
  unit_id: string | null
  qr_payload: string
  weight_g: number | null
  context: 'ISSUE' | 'WEIGH_IN'
  local: boolean
}

export interface RunOutputView {
  container_id: string
  unit_id: string
  stream_key: string
  qr_payload: string
  sack_type_id: string | null
  tare_g: number
  read_g: number
  net_g: number
  stream: 'MAIN' | 'CONV'
  grade: string | null
  voided: boolean
  local: boolean
  /** The server knows a sample of this batch (site list; absent on older servers). */
  sampled?: boolean
  /** The batch's status on the server (site list), e.g. CONSUMED. */
  unit_status?: string | null
}

export interface SampleRef {
  sample_id: string
  unit_id: string
  seal_payload: string
  status: string
  purpose: string | null
  local: boolean
}

export interface RunView {
  id: string
  code: string
  equipment_id: string
  step_code: string
  status: RunStatus
  stream: 'MAIN' | 'CONV'
  crop_id: string
  crop_name: string | null
  origin_unit_ids: string[]
  farmer_id: string | null
  farmer_code: string | null
  season_code: string | null
  yield_band_id: string | null
  flush_required: boolean
  flush_min_g: number | null
  allergen_clean_required: boolean
  previous_run: Partial<LastRun> | null
  operator_id: string | null
  cleanout_by_id: string | null
  opened_at: string | null
  cleanout_confirmed_at: string | null
  started_at: string | null
  ended_at: string | null
  inputs: RunInputView[]
  outputs: RunOutputView[]
  zero_streams: string[]
  mixing_reason: string | null
  params: Record<string, unknown>
  /** The server's balance (after the close synced). */
  balance: RunBalance | null
  open_exceptions: Array<{ id: string; type: string; kinds: string[] }>
  samples: SampleRef[]
  /** Containers issued to this run (server tasks + this phone's issues). */
  issued: Map<string, number | null>
  /** Made on this phone and not in the downloaded pack yet. */
  local: boolean
  /** A close was made on this phone (provisional until synced). */
  closed_local: boolean
  corrections: CorrectRunPayload[]
  sync: SyncMark
}

export interface IssueTaskView {
  shipment_id: string
  code: string
  run_id: string
  run_code: string
  step_code: string | null
  status: string
  unit_ids: string[]
  container_ids: string[]
  /** container → net g (null when the server did not say). */
  issued: Map<string, number | null>
  moisture_unit_ids: Set<string>
  closed_local: boolean
  sync: SyncMark
}

export interface MachineView extends Machine {
  /** Bound on this phone, not yet in the pack. */
  label_local: boolean
}

/** A loose zod type without its `[key: string]: unknown` index, so Omit keeps the known keys. */
type Known<T> = { [K in keyof T as string extends K ? never : K]: T[K] }

export interface PackRunView extends Omit<Known<PackRun>, 'inputs' | 'outputs'> {
  inputs: RunInputView[]
  outputs: RunOutputView[]
  first_pouch: { match: boolean; scanned_text: string } | null
  last_pouch: { match: boolean; scanned_text: string } | null
  spot_weights: Array<{ value_g: number; position: number }>
  close: PackClosePayload | null
  sync: SyncMark
}

export interface ProcessState {
  pack: SitePack
  downloadedAt: number
  siteId: string
  steps: Map<string, Step>
  machines: Map<string, MachineView>
  machineByLabel: Map<string, MachineView>
  units: Map<string, SiteUnit>
  bags: Map<string, BagView>
  bagByPayload: Map<string, BagView>
  runs: Map<string, RunView>
  issueTasks: Map<string, IssueTaskView>
  packRuns: Map<string, PackRunView>
  packLots: Map<string, PackLot>
  deadTags: Map<string, 'VOID' | 'RETIRED'>
  /** Tag / seal / label payloads bound or used up on this phone or known used on the server. */
  usedTags: Set<string>
  /** Sealed samples by the batch they came from. */
  samplesByUnit: Map<string, SampleRef[]>
}

const norm = (c: string) => c.trim().toUpperCase()

const DONE = new Set(['APPLIED', 'DUPLICATE', 'HELD'])

/** Did the server have this command when the pack was made? Then the pack already shows it. */
function inPack(rec: OutboxRecord, downloadedAt: number): boolean {
  return rec.acked_at !== null && rec.acked_at <= downloadedAt
}

function markCommand(mark: SyncMark, rec: OutboxRecord): void {
  if (!DONE.has(rec.status)) mark.pending_command_id = rec.id
  if (rec.status === 'HELD') {
    mark.held = true
    mark.exception_ids.push(...(rec.result?.exception_ids ?? []))
  }
  if (rec.status === 'ERROR') mark.error = true
}

/** The flush minimum from a sync answer: grams, or the server's kilos. */
function flushMinFrom(ss: Record<string, unknown>): number | null {
  if (typeof ss.flush_min_g === 'number') return ss.flush_min_g
  if (typeof ss.flush_min_kg === 'number') return Math.round(ss.flush_min_kg * 1000)
  if (typeof ss.flush_min_kg === 'string' && /^\d+(\.\d+)?$/.test(ss.flush_min_kg)) return Math.round(Number(ss.flush_min_kg) * 1000)
  return null
}

function serverState(rec: OutboxRecord): Record<string, unknown> | null {
  const s = rec.result?.server_state
  return s && typeof s === 'object' ? (s as Record<string, unknown>) : null
}

function runFromSite(r: SiteRun): RunView {
  return {
    id: r.id,
    code: r.code,
    equipment_id: r.equipment_id,
    step_code: r.step_code,
    status: r.status,
    stream: r.stream,
    crop_id: r.crop_id,
    crop_name: r.crop_name ?? null,
    origin_unit_ids: r.origin_unit_ids.length
      ? r.origin_unit_ids
      : r.origin_unit_id
        ? [r.origin_unit_id]
        : [],
    farmer_id: r.farmer_id ?? null,
    farmer_code: r.farmer_code ?? null,
    season_code: r.season_code ?? null,
    yield_band_id: r.yield_band_id ?? null,
    flush_required: r.flush_required,
    flush_min_g: r.flush_min_g ?? null,
    allergen_clean_required: r.allergen_clean_required,
    previous_run: r.previous_run ?? null,
    operator_id: r.operator_id ?? null,
    cleanout_by_id: r.cleanout_by_id ?? null,
    opened_at: r.opened_at ?? null,
    cleanout_confirmed_at: r.cleanout_confirmed_at ?? null,
    started_at: r.started_at ?? null,
    ended_at: r.ended_at ?? null,
    inputs: r.inputs.map((i) => ({
      id: i.id,
      container_id: i.container_id,
      unit_id: i.unit_id,
      qr_payload: i.qr_payload,
      weight_g: i.weight_g,
      context: i.context,
      local: false,
    })),
    outputs: r.outputs.map((o) => ({
      container_id: o.container_id,
      unit_id: o.unit_id,
      stream_key: o.stream_key,
      qr_payload: o.qr_payload,
      sack_type_id: o.sack_type_id ?? null,
      tare_g: o.tare_g,
      read_g: o.read_g,
      net_g: o.net_g,
      stream: o.stream,
      grade: null,
      voided: o.status === 'VOID',
      local: false,
      sampled: o.sampled ?? false,
      unit_status: o.unit_status ?? null,
    })),
    zero_streams: [...r.zero_streams],
    mixing_reason: r.mixing_reason ?? null,
    params: { ...r.params },
    balance: r.balance ?? null,
    open_exceptions: r.open_exceptions.map((e) => ({ ...e, kinds: [...e.kinds] })),
    samples: r.samples.map((s) => ({
      sample_id: s.sample_id,
      unit_id: s.unit_id,
      seal_payload: s.seal_payload,
      status: s.status,
      purpose: s.purpose ?? null,
      local: false,
    })),
    issued: new Map(),
    local: false,
    closed_local: false,
    corrections: [],
    sync: emptyMark(),
  }
}

/** Output stream of a sack: the step registry says CONV or "follows the run". */
export function outputStream(
  step: Step | undefined,
  streamKey: string,
  runStream: 'MAIN' | 'CONV',
): 'MAIN' | 'CONV' {
  if (streamKey === 'FLUSH' || streamKey === 'SPILLAGE') return 'CONV'
  if (streamKey === 'LEFTOVER') return runStream
  const out = step?.outputs.find((o) => o.key === streamKey)
  if (!out) return 'CONV'
  return out.stream === 'CONV' ? 'CONV' : runStream
}

export function projectProcess(
  stored: StoredSitePack,
  outbox: readonly OutboxRecord[],
): ProcessState {
  const { pack, downloaded_at } = stored
  const steps = new Map(pack.steps.map((s) => [s.step_code, s]))
  const machines = new Map<string, MachineView>(
    pack.machines.map((m) => [m.id, { ...m, label_local: false }]),
  )
  const units = new Map(pack.lots.map((u) => [u.id, { ...u }]))
  const bags = new Map<string, BagView>(
    pack.bags.map((b) => [b.container_id, { ...b, issued_run_id: null, issue_net_g: null }]),
  )
  const deadTags = new Map(pack.dead_tags.map((d) => [norm(d.qr_payload), d.status]))
  const usedTags = new Set<string>()
  for (const r of pack.rolls) for (const u of r.used) usedTags.add(norm(u))

  const runs = new Map<string, RunView>(pack.runs.map((r) => [r.id, runFromSite(r)]))
  const issueTasks = new Map<string, IssueTaskView>(
    pack.issue_tasks.map((t) => [
      t.shipment_id,
      {
        shipment_id: t.shipment_id,
        code: t.code,
        run_id: t.run_id,
        run_code: t.run_code,
        step_code: t.step_code ?? null,
        status: t.status,
        unit_ids: [...t.unit_ids],
        container_ids: [...t.container_ids],
        issued: new Map(t.issued.map((i) => [i.container_id, i.net_g ?? null])),
        moisture_unit_ids: new Set(t.moisture_unit_ids),
        closed_local: false,
        sync: emptyMark(),
      },
    ]),
  )
  const packRuns = new Map<string, PackRunView>(
    pack.pack_runs.map((r) => [
      r.id,
      {
        ...r,
        inputs: r.inputs.map((i) => ({
          id: i.id,
          container_id: i.container_id,
          unit_id: i.unit_id,
          qr_payload: i.qr_payload,
          weight_g: i.weight_g,
          context: i.context,
          local: false,
        })),
        outputs: r.outputs.map((o) => ({
          container_id: o.container_id,
          unit_id: o.unit_id,
          stream_key: o.stream_key,
          qr_payload: o.qr_payload,
          sack_type_id: o.sack_type_id ?? null,
          tare_g: o.tare_g,
          read_g: o.read_g,
          net_g: o.net_g,
          stream: o.stream,
          grade: null,
          voided: o.status === 'VOID',
          local: false,
        })),
        first_pouch: null,
        last_pouch: null,
        spot_weights: [],
        close: null,
        sync: emptyMark(),
      },
    ]),
  )
  const packLots = new Map<string, PackLot>(pack.pack_lots.map((l) => [l.id, { ...l }]))
  for (const r of pack.pack_runs) if (r.pack_lot && !packLots.has(r.pack_lot.id)) packLots.set(r.pack_lot.id, r.pack_lot)

  // Bags issued per run from the server's tasks.
  for (const t of issueTasks.values()) {
    for (const [cid, net] of t.issued) {
      const b = bags.get(cid)
      if (b) {
        b.issued_run_id = t.run_id
        b.issue_net_g = net ?? b.net_g_current ?? null
      }
    }
  }
  for (const r of runs.values()) {
    for (const o of r.outputs) usedTags.add(norm(o.qr_payload))
    for (const i of r.inputs) usedTags.add(norm(i.qr_payload))
    for (const s of r.samples) usedTags.add(norm(s.seal_payload))
  }
  for (const m of machines.values()) if (m.label_payload) usedTags.add(norm(m.label_payload))

  const local = outbox
    .filter((r) => PROCESS_TYPES.has(r.type) && !inPack(r, downloaded_at))
    .sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0))

  const runOf = (runId: string): RunView | undefined => runs.get(runId)
  // Samples of batches whose run is not in the pack (e.g. a confirming sample of a held batch).
  const localSamples: SampleRef[] = []

  for (const rec of local) {
    switch (rec.type) {
      case 'bind_machine_label': {
        const p = rec.payload as BindMachineLabelPayload
        const m = machines.get(p.equipment_id)
        if (m && !m.label_payload) {
          m.label_payload = p.qr_payload
          m.label_local = true
        }
        usedTags.add(norm(p.qr_payload))
        break
      }
      case 'open_run': {
        const p = rec.payload as OpenRunPayload
        if (!runs.has(p.run_id)) {
          const origin = units.get(p.lot_unit_ids[0]!)
          const band = pack.yield_bands.find((b) => b.id === p.yield_band_id)
          const last = pack.last_runs.find((l) => l.equipment_id === p.equipment_id)
          runs.set(p.run_id, {
            id: p.run_id,
            code: p.code_proposed,
            equipment_id: p.equipment_id,
            step_code: p.step_code,
            status: 'OPEN',
            stream: origin?.stream ?? 'CONV',
            crop_id: origin?.crop_id ?? '',
            crop_name: origin?.crop_name ?? null,
            origin_unit_ids: [...p.lot_unit_ids],
            farmer_id: origin?.farmer_id ?? null,
            farmer_code: origin?.farmer_code ?? null,
            season_code: origin?.season_code ?? null,
            yield_band_id: p.yield_band_id,
            flush_required: p.flush_required,
            flush_min_g: band?.min_flush_g ?? null,
            allergen_clean_required: p.allergen_clean_required,
            previous_run: last ?? null,
            operator_id: rec.actor_id,
            cleanout_by_id: null,
            opened_at: rec.occurred_at,
            cleanout_confirmed_at: null,
            started_at: null,
            ended_at: null,
            inputs: [],
            outputs: [],
            zero_streams: [],
            mixing_reason: null,
            params: {},
            balance: null,
            open_exceptions: [],
            samples: [],
            issued: new Map(),
            local: true,
            closed_local: false,
            corrections: [],
            sync: emptyMark(),
          })
        }
        const r = runs.get(p.run_id)!
        markCommand(r.sync, rec)
        const ss = serverState(rec)
        if (ss && typeof ss.code === 'string') r.code = ss.code
        if (ss && ss.flush_required === true) r.flush_required = true
        if (ss) r.flush_min_g = flushMinFrom(ss) ?? r.flush_min_g
        if (ss && ss.allergen_clean_required === true) r.allergen_clean_required = true
        if (ss && (ss.stream === 'MAIN' || ss.stream === 'CONV')) r.stream = ss.stream
        break
      }
      case 'cancel_run': {
        const p = rec.payload as CancelRunPayload
        const r = runOf(p.run_id)
        if (!r) break
        r.status = 'CANCELLED'
        markCommand(r.sync, rec)
        const ss = serverState(rec)
        if (ss && ss.status === 'OPEN') r.status = 'OPEN'
        break
      }
      case 'issue_to_run': {
        const p = rec.payload as IssueToRunPayload
        const net = p.weight_read_g - p.tare_g
        const b = bags.get(p.container_id)
        if (b) {
          b.status = 'ISSUED'
          b.issued_run_id = p.run_id
          b.issue_net_g = net
          b.net_g_current = net
        }
        const t = issueTasks.get(p.shipment_id)
        if (t) {
          t.issued.set(p.container_id, net)
          if (p.moisture_pct && b) t.moisture_unit_ids.add(b.unit_id)
          markCommand(t.sync, rec)
        }
        break
      }
      case 'close_issue': {
        const p = rec.payload as CloseIssuePayload
        const t = issueTasks.get(p.shipment_id)
        if (t) {
          t.closed_local = true
          t.status = p.missing_container_ids.length ? 'RECEIVED_SHORT' : 'RECEIVED'
          markCommand(t.sync, rec)
        }
        break
      }
      case 'confirm_cleanout': {
        const p = rec.payload as ConfirmCleanoutPayload
        const r = runOf(p.run_id)
        if (!r) break
        r.cleanout_confirmed_at ??= rec.occurred_at
        r.cleanout_by_id ??= rec.actor_id
        markCommand(r.sync, rec)
        const ss = serverState(rec)
        if (ss && ss.flush_required === true) r.flush_required = true
        if (ss) r.flush_min_g = flushMinFrom(ss) ?? r.flush_min_g
        break
      }
      case 'weigh_in': {
        const p = rec.payload as WeighInPayload
        const r = runOf(p.run_id)
        const pr = packRuns.get(p.run_id)
        if (!r && !pr) break // a run of another site (this phone moved sites)
        const b = p.container_id ? bags.get(p.container_id) : undefined
        if (b) b.status = 'CONSUMED'
        usedTags.add(norm(p.qr_payload))
        // No reading (V1, or the issue was seen): the bag's last weight — or, once the bag is used
        // up and gone from a newer pack, the weight the server answered for this input.
        const ss = serverState(rec)
        const weight =
          p.weight_read_g !== null && !p.issue_seen
            ? p.weight_read_g - (b?.tare_g ?? 0)
            : p.weight_read_g !== null && pr
              ? p.weight_read_g - (b?.tare_g ?? 0)
              : typeof ss?.net_g === 'number'
                ? ss.net_g
                : (b?.issue_net_g ?? b?.net_g_current ?? null)
        const input: RunInputView = {
          id: p.run_input_id,
          container_id: p.container_id,
          unit_id: b?.unit_id ?? null,
          qr_payload: p.qr_payload,
          weight_g: weight,
          context: p.issue_seen && !pr ? 'ISSUE' : 'WEIGH_IN',
          local: true,
        }
        // The pack's copy of this input (same id, or the same bag in this run) wins.
        const sameInput = (i: RunInputView) =>
          i.id === input.id || (!!input.container_id && i.container_id === input.container_id)
        if (r) {
          if (!r.inputs.some(sameInput)) r.inputs.push(input)
          if (r.status === 'OPEN') r.status = 'RUNNING'
          r.started_at ??= rec.occurred_at
          markCommand(r.sync, rec)
        } else if (pr) {
          if (!pr.inputs.some(sameInput)) pr.inputs.push(input)
          if (pr.status === 'OPEN') pr.status = 'RUNNING'
          markCommand(pr.sync, rec)
        }
        break
      }
      case 'report_mixing': {
        const p = rec.payload as ReportMixingPayload
        const r = runOf(p.run_id)
        if (!r) break
        r.stream = 'CONV'
        r.mixing_reason = p.reason_code
        for (const o of r.outputs) o.stream = 'CONV'
        markCommand(r.sync, rec)
        break
      }
      case 'record_output': {
        const p = rec.payload as RecordOutputPayload
        const r = runOf(p.run_id)
        const pr = packRuns.get(p.run_id)
        if (r || pr) usedTags.add(norm(p.qr_payload))
        const runStream = r?.stream ?? 'MAIN'
        const out: RunOutputView = {
          container_id: p.container_id,
          unit_id: p.unit_id,
          stream_key: p.stream_key,
          qr_payload: p.qr_payload,
          sack_type_id: p.sack_type_id,
          tare_g: p.tare_g,
          read_g: p.weight_read_g,
          net_g: p.weight_read_g - p.tare_g,
          stream: pr
            ? p.stream_key === 'SPILLAGE'
              ? 'CONV'
              : 'MAIN'
            : outputStream(steps.get(r?.step_code ?? ''), p.stream_key, runStream),
          grade: p.grade,
          voided: false,
          local: true,
        }
        if (r) {
          if (!r.outputs.some((o) => o.container_id === out.container_id)) r.outputs.push(out)
          markCommand(r.sync, rec)
        } else if (pr) {
          if (!pr.outputs.some((o) => o.container_id === out.container_id)) pr.outputs.push(out)
          markCommand(pr.sync, rec)
        }
        break
      }
      case 'void_output': {
        const p = rec.payload as VoidOutputPayload
        const target = runOf(p.run_id) ?? packRuns.get(p.run_id)
        if (!target) break
        const o = target.outputs.find((x) => x.container_id === p.container_id)
        if (o) o.voided = true
        markCommand(target.sync, rec)
        break
      }
      case 'close_run': {
        const p = rec.payload as CloseRunPayload
        const r = runOf(p.run_id)
        if (!r) break
        r.ended_at = p.ended_at
        r.zero_streams = [...p.zero_streams]
        r.params = { ...r.params, ...p.params }
        r.closed_local = true
        if (r.status === 'OPEN' || r.status === 'RUNNING') r.status = 'CLOSED'
        markCommand(r.sync, rec)
        const ss = serverState(rec)
        if (ss) {
          if (ss.status === 'BALANCED' || ss.status === 'EXCEPTION') r.status = ss.status
          if (ss.balance && typeof ss.balance === 'object') r.balance = ss.balance as RunBalance
        }
        break
      }
      case 'seal_sample': {
        const p = rec.payload as SealSamplePayload
        usedTags.add(norm(p.seal_qr_payload))
        const unit = units.get(p.subject_id)
        const r = unit?.run_id ? runOf(unit.run_id) : undefined
        const s: SampleRef = {
          sample_id: p.sample_id,
          unit_id: p.subject_id,
          seal_payload: p.seal_qr_payload,
          status: 'SEALED',
          purpose: p.purpose,
          local: true,
        }
        if (r) {
          if (!r.samples.some((x) => x.sample_id === s.sample_id)) r.samples.push(s)
          markCommand(r.sync, rec)
        } else {
          // A batch whose run is not in the pack (e.g. a held batch for a confirming sample).
          const holder = [...runs.values()].find((x) => x.outputs.some((o) => o.unit_id === p.subject_id))
          if (holder && !holder.samples.some((x) => x.sample_id === s.sample_id)) holder.samples.push(s)
          localSamples.push(s)
        }
        break
      }
      case 'correct_run': {
        const p = rec.payload as CorrectRunCommandPayload
        const r = runOf(p.run_id)
        if (!r) break
        // A pouch count names no sack: only sack corrections carry a tag.
        if (p.kind !== 'POUCH_COUNT') {
          r.corrections.push(p)
          usedTags.add(norm(p.qr_payload))
        }
        markCommand(r.sync, rec)
        const ss = serverState(rec)
        if (ss?.balance && typeof ss.balance === 'object') r.balance = ss.balance as RunBalance
        break
      }
      case 'decide_run': {
        const p = rec.payload as DecideRunPayload
        const r = runOf(p.run_id)
        if (!r) break
        // A refused decision changed nothing: the run's next command does not wait on it.
        if (rec.status === 'ERROR') break
        markCommand(r.sync, rec)
        const ss = serverState(rec)
        if (ss && (ss.run_status === 'BALANCED' || ss.run_status === 'EXCEPTION')) r.status = ss.run_status
        break
      }
      case 'verify_pouch': {
        const p = rec.payload as VerifyPouchPayload
        const pr = packRuns.get(p.run_id)
        if (!pr) break
        const v = { match: p.match, scanned_text: p.scanned_text }
        if (p.position === 'FIRST') {
          // A later matching first pouch replaces a mismatched one (reprinted after re-keying).
          if (!pr.first_pouch || !pr.first_pouch.match) pr.first_pouch = v
        } else pr.last_pouch = v
        markCommand(pr.sync, rec)
        break
      }
      case 'weigh_pouches': {
        const p = rec.payload as WeighPouchesPayload
        const pr = packRuns.get(p.run_id)
        if (!pr) break
        for (const w of p.weights) pr.spot_weights.push({ value_g: w.value_g, position: w.position })
        markCommand(pr.sync, rec)
        break
      }
      case 'pack_close': {
        const p = rec.payload as PackClosePayload
        const pr = packRuns.get(p.run_id)
        if (!pr) break
        pr.close = p
        if (pr.status === 'OPEN' || pr.status === 'RUNNING') pr.status = 'CLOSED'
        const lot = pr.pack_lot ? packLots.get(pr.pack_lot.id) : undefined
        if (lot && lot.status === 'PACKING') {
          lot.status = 'CLOSED'
          lot.pouch_count = p.pouches_good
        }
        if (pr.pack_lot && pr.pack_lot.status === 'PACKING')
          pr.pack_lot = { ...pr.pack_lot, status: 'CLOSED', pouch_count: p.pouches_good }
        markCommand(pr.sync, rec)
        break
      }
    }
  }

  // Bags issued to each run (server tasks + this phone).
  for (const b of bags.values()) {
    if (!b.issued_run_id) continue
    const r = runs.get(b.issued_run_id) ?? packRuns.get(b.issued_run_id)
    if (r && 'issued' in r) r.issued.set(b.container_id, b.issue_net_g)
  }
  for (const pr of packRuns.values())
    for (const cid of pr.issued_container_ids) {
      const b = bags.get(cid)
      if (b && !b.issued_run_id) {
        b.issued_run_id = pr.id
        b.issue_net_g = b.net_g_current ?? null
      }
    }

  const bagByPayload = new Map<string, BagView>()
  for (const b of bags.values()) bagByPayload.set(norm(b.qr_payload), b)
  const machineByLabel = new Map<string, MachineView>()
  for (const m of machines.values())
    if (m.label_payload) machineByLabel.set(norm(m.label_payload), m)

  const samplesByUnit = new Map<string, SampleRef[]>()
  for (const r of runs.values())
    for (const s of r.samples) samplesByUnit.set(s.unit_id, [...(samplesByUnit.get(s.unit_id) ?? []), s])
  for (const s of localSamples)
    if (!(samplesByUnit.get(s.unit_id) ?? []).some((x) => x.sample_id === s.sample_id))
      samplesByUnit.set(s.unit_id, [...(samplesByUnit.get(s.unit_id) ?? []), s])

  return {
    pack,
    downloadedAt: downloaded_at,
    siteId: pack.site_id,
    steps,
    machines,
    machineByLabel,
    units,
    bags,
    bagByPayload,
    runs,
    issueTasks,
    packRuns,
    packLots,
    deadTags,
    usedTags,
    samplesByUnit,
  }
}

/** Live outputs (not voided). */
export function liveOutputs(r: { outputs: RunOutputView[] }): RunOutputView[] {
  return r.outputs.filter((o) => !o.voided)
}

/** Last command made on this phone for a run that the server may not have yet. */
export function runParentIds(r: { sync: SyncMark }): string[] {
  return r.sync.pending_command_id ? [r.sync.pending_command_id] : []
}

export function isRunLive(status: RunStatus): boolean {
  return status === 'OPEN' || status === 'RUNNING'
}
